import { useEffect, useRef, useState } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { firebaseApp } from '../firebase-config';
import { useUser } from '../contexts/userContext';
import { authService } from '../services/auth.service';

type UseUserMenuResult = {
  authentication: ReturnType<typeof getAuth>;
  initials: string;
  isMenuOpen: boolean;
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
  menuRef: RefObject<HTMLDivElement>;
  signOutUser: () => void;
};

export function useUserMenu(): UseUserMenuResult {
  const { dispatch } = useUser();
  const authentication = getAuth(firebaseApp);

  const [initials, setInitials] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const displayName = authentication.currentUser?.displayName ?? '';
    const fullName = (displayName || 'First Last').split(' ');
    const initialsName = fullName[0].charAt(0) + (fullName[1] ?? '').charAt(0);
    setInitials(initialsName);
  }, [authService.editUsername, authentication.currentUser?.displayName]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!isMenuOpen) return;
      const target = e.target as Node | null;
      if (target && menuRef.current && !menuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  const signOutUser = () => {
    signOut(authentication)
      .then(() => {
        dispatch({ type: 'logout' });
      })
      .catch(() => undefined);
  };

  return {
    authentication,
    initials,
    isMenuOpen,
    setIsMenuOpen,
    menuRef,
    signOutUser
  };
}


